/* 
 * Componente de lectura de datos meteorológicos via API
 *
 * useMeteoData.js
 * 
*/
import { useState, useEffect } from "react";

import config from "../config.json";
const sensors = config.sensors

export const useMeteoData = (stations = ["speculoos"], time_range = "PT2H") => {
  // Normalizar entrada: si recibe string, convertir a array
  const stationList = Array.isArray(stations) ? stations : [stations];
  
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const obtenerDatosPorEstacion = async (station) => {
    try {
      // Datos para el valor mas reciente (latest)
      const response_latest = await fetch(`https://ocan.iac.es/meteo/api/v1/station/${station}/last`);
      const latest_json = await response_latest.json();

      // Datos para la serie temporal para graficos (history)
      const response_history = await fetch(`https://ocan.iac.es/meteo/api/v1/station/${station}/recent?time_range=${time_range}`);
      const history_json = await response_history.json();

      // Reformatear el array history_json

      let history = {};

      sensors.forEach(function(sensor) {
        history[sensor] = [];
      });
      
      for (const measurement of history_json) {
        // convierto 2023-10-23T21:00:00" a timestamp
        let timestamp = new Date(measurement.time).getTime();

        // El seeing da valor 0 cuando no ha medido, pero con fecha
        // en ese caso se ignora esa entrada (seeing=0)
        sensors.forEach(function(sensor) {
          if (sensor != "seeing" || sensor == "seeing" && measurement[sensor] != 0) {
            history[sensor].push([timestamp, measurement[sensor]]);
          }
        });
      }

      return {
        latestData: latest_json,
        historyData: history,
        error: null
      };

    } catch (e) {
      console.error(`Error fetching data for station ${station}:`, e);
      return {
        latestData: {},
        historyData: {
          'wind_speed': [], 
          'temperature': [], 
          'humidity': [], 
          'dust': []
        },
        error: e.message
      };
    }
  };

  const obtenerDatos = async () => {
    try {
      setLoading(true);
      setError(null);

      const promises = stationList.map(station =>
        obtenerDatosPorEstacion(station).then(result => ({ station, ...result }))
      );

      const results = await Promise.all(promises);

      setData(prev => {
        const newData = { ...prev }; // conserva datos anteriores
        results.forEach(({ station, latestData, historyData, error }) => {
          if (!error) {  // solo actualiza si no hubo error
            newData[station] = { latestData, historyData };
          }
        });
        return newData;
      });

      const errors = results.filter(r => r.error).map(r => `${r.station}: ${r.error}`);
      if (errors.length) setError(errors.join('; '));

    } catch (e) {
      console.error("Error general:", e);
      setError(e.message);
      // No tocamos data, se quedan los últimos válidos
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (stationList.length > 0) {
      obtenerDatos(); // llamada inicial
      const intervalo = setInterval(obtenerDatos, 5 * 60 * 1000); // cada 5 minutos

      return () => clearInterval(intervalo); // limpia al desmontar
    }
  }, [JSON.stringify(stationList.sort()), time_range]); // Dependencia estable para el array

  // Para compatibilidad hacia atrás con una sola estación
  const getSingleStationData = () => {
    if (stationList.length === 1) {
      const singleStation = stationList[0];
      return {
        latestData: data[singleStation]?.latestData || {},
        historyData: data[singleStation]?.historyData || {
          'wind_speed': [], 'temperature': [], 'humidity': [], 'dust': []
        }
      };
    }
    return { latestData: {}, historyData: {} };
  };

  return {
    // Datos multi-estación (nuevo formato)
    data, // { "station1": { latestData, historyData }, "station2": { ... }, ... }
    
    // Compatibilidad hacia atrás (formato anterior para una sola estación)
    ...getSingleStationData(),
    
    loading,
    error,
    refetch: obtenerDatos,
    
    // Utilidades adicionales
    stations: stationList,
    getStationData: (station) => data[station] || { latestData: {}, historyData: {} }
  };
};